import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Pencil, Trash2, Radio } from "lucide-react";

export const EventList = ({ events, activeId, onActivate, onEdit, onDelete }) => {
  if (!events.length) {
    return (
      <div
        data-testid="events-empty"
        className="rounded-2xl border border-dashed border-border bg-card p-10 text-center"
      >
        <p className="font-display text-lg font-bold tracking-tight">No events yet</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Create your first event (like a meetup or expo) to get started.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="events-list">
      {events.map((ev, i) => {
        const isActive = ev.id === activeId;
        return (
          <div
            key={ev.id}
            data-testid={`event-card-${ev.id}`}
            style={{ animationDelay: `${i * 60}ms` }}
            className={`fade-up rounded-xl border bg-card p-5 transition-[border-color,transform] duration-200 hover:-translate-y-0.5 ${
              isActive ? "border-accent glow-green" : "border-border"
            }`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-display text-lg font-bold tracking-tight truncate">{ev.name}</h3>
                  {isActive && (
                    <Badge
                      data-testid={`event-live-badge-${ev.id}`}
                      className="rounded-full bg-accent text-accent-foreground hover:bg-accent gap-1.5"
                    >
                      <span className="relative flex h-2 w-2">
                        <span className="live-dot absolute inline-flex h-full w-full rounded-full bg-white" />
                      </span>
                      LIVE
                    </Badge>
                  )}
                </div>
                <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap line-clamp-2">
                  {ev.message}
                </p>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2">
              <Button
                data-testid={`event-activate-btn-${ev.id}`}
                onClick={() => onActivate(ev.id)}
                disabled={isActive}
                className={`rounded-full active:scale-95 transition-transform duration-150 ${
                  isActive
                    ? "bg-accent/15 text-accent hover:bg-accent/15 cursor-default"
                    : "bg-accent text-accent-foreground hover:bg-accent/90"
                }`}
                size="sm"
              >
                <Radio className="h-4 w-4 mr-1.5" />
                {isActive ? "Live now" : "Set Live"}
              </Button>
              <Button
                data-testid={`event-edit-btn-${ev.id}`}
                onClick={() => onEdit(ev)}
                variant="outline"
                size="sm"
                className="rounded-full active:scale-95 transition-transform duration-150"
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    data-testid={`event-delete-btn-${ev.id}`}
                    variant="outline"
                    size="sm"
                    className="rounded-full text-destructive hover:text-destructive active:scale-95 transition-transform duration-150"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="delete-confirm-dialog" className="rounded-2xl">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete "{ev.name}"?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This can't be undone. Scan history for this event stays in analytics.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      data-testid={`event-delete-confirm-${ev.id}`}
                      onClick={() => onDelete(ev.id)}
                      className="rounded-full bg-destructive hover:bg-destructive/90"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        );
      })}
    </div>
  );
};
